/**
 * collabBackupStatus — per-image "is a P2P replica out there yet?" state for the
 * free-mode durability UX (D1).
 *
 * A free-mode image lives only in OPFS; until another online peer has pulled a
 * copy, closing this tab loses it. So on a local upload the uploader marks the
 * sha `backing-up`; `useEditorDoc`'s BlobExchange `onServed` callback marks it
 * `backed` once a peer takes a copy. ImageWidget shows a "keep this tab open"
 * chip while a sha is `backing-up`.
 *
 * Keyed by sha (a global content address). Module-level + a tiny store so the
 * widget re-renders on status change (via useSyncExternalStore). Only ever used
 * when collab is active; solo mode never marks anything.
 */
import { useSyncExternalStore } from "react";

export type BackupStatus = "backing-up" | "backed";

const status = new Map<string, BackupStatus>();
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((l) => l());

/** A local image was just added and isn't replicated yet. No-op once backed. */
export function markBackingUp(sha: string): void {
  if (status.get(sha) === "backed") return;
  status.set(sha, "backing-up");
  notify();
}

/** A peer took a copy → a replica now exists elsewhere. */
export function markBackedUp(sha: string): void {
  if (status.get(sha) === "backed") return;
  status.set(sha, "backed");
  notify();
}

/** Drop all tracked status. Called on collab teardown so the module-level map
 *  doesn't accumulate shas across sessions/drafts (R6); the chips are moot once
 *  the session ends anyway. */
export function resetBackupStatus(): void {
  if (status.size === 0) return;
  status.clear();
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Reactive backup status for a sha (undefined = untracked, e.g. solo mode). */
export function useBackupStatus(
  sha: string | undefined,
): BackupStatus | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (sha ? status.get(sha) : undefined),
    () => undefined,
  );
}
